/**
 * CourseModuleRegistry — tipos núcleo (F2-A, Diseño A).
 *
 * La espina Moodle de Space Academy: dentro de una `unit` viven `course_module`
 * de kinds enchufables (lesson | resource | assignment | forum | video |
 * live_session | …). Igual que `learning-blocks`, el CORE nunca conoce los
 * kinds concretos: itera módulos y delega en el plugin registrado. Añadir un
 * kind = registrar un plugin (F3 añade assignment/forum/video/live_session sin
 * tocar el core — invariante probado en tests).
 *
 * Los componentes de UI (authoring/player) son GENÉRICOS (`TComponent`) para
 * que este paquete siga siendo framework-agnóstico: la app web instancia el
 * registry con sus componentes Solid lazy.
 */

import type { FeatureManifest } from "../country/packs";

/** Resultado de validar una config/settings de módulo (espejo de learning-blocks). */
export interface ModuleValidationOk<T> {
  ok: true;
  value: T;
}
export interface ModuleValidationErr {
  ok: false;
  errors: string[];
}
export type ModuleValidationResult<T> = ModuleValidationOk<T> | ModuleValidationErr;

/**
 * Validador tipado (sin zod en deps): `parse` valida y NORMALIZA. La forma es
 * compatible con un futuro swap a zod (`schema.safeParse` se adapta trivial).
 */
export interface SchemaValidator<T> {
  parse(value: unknown): ModuleValidationResult<T>;
}

/** Fila genérica de `course_module` tal como llega del cliente local-first. */
export interface CourseModuleRow {
  id: string;
  unit_id: string | null;
  course_id: string | null;
  kind: string | null;
  ref_id: string | null;
  position: number | null;
  visibility: string | null;
  completion_rule: string | null;
  config: string | null;
}

/** Contexto para computar la contribución de completitud de un módulo (0..1). */
export interface ModuleCompletionCtx {
  module: CourseModuleRow;
  /** Progreso de lección del alumno para ref_id (si aplica al kind). */
  lessonProgress?: { status: string | null; score: number | null } | null;
}

/** Contexto para computar la contribución de nota de un módulo (0..1). */
export interface ModuleGradeCtx {
  module: CourseModuleRow;
  lessonProgress?: { status: string | null; score: number | null } | null;
}

/**
 * Un kind de módulo enchufable.
 *
 * `manifest.key` sigue la convención del catálogo auto-descriptivo:
 * `academy.module.<kind>` — el manifest alimenta `pnpm gen:features` (F2-D)
 * y de ahí el `FeatureCatalog` Rust (ninguna feature oculta).
 */
export interface CourseModulePlugin<
  TComponent = unknown,
  Config = Record<string, unknown>,
  Settings = Record<string, unknown>,
> {
  /** Identificador estable del kind (p. ej. "lesson", "resource"). */
  readonly kind: string;
  /** Metadatos auto-descriptivos (catálogo de features). */
  readonly manifest: FeatureManifest;
  /** Valida y normaliza `course_module.config`. */
  readonly configSchema: SchemaValidator<Config>;
  /** Valida los settings por-cohorte (cohort_module_setting.config). */
  readonly settingsSchema?: SchemaValidator<Settings>;
  /** Loader LAZY del editor de autoría del módulo (la web lo inyecta). */
  readonly authoringComponent?: () => Promise<TComponent>;
  /** Loader LAZY del componente de consumo del módulo (la web lo inyecta). */
  readonly playerComponent?: () => Promise<TComponent>;
  /**
   * Contribución de completitud del módulo (0..1) o `null` si el kind no
   * aporta al criterio de completitud del curso (p. ej. un recurso).
   */
  completionContribution?(ctx: ModuleCompletionCtx): number | null;
  /** Contribución de nota (0..1) o `null` si el kind no puntúa. */
  gradeContribution?(ctx: ModuleGradeCtx): number | null;
}

/** Type guard: ¿es un objeto plano (Record)? Sin aserciones inseguras. */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Helper de validador permisivo: acepta cualquier objeto (kinds sin config). */
export function anyObjectValidator(): SchemaValidator<Record<string, unknown>> {
  return {
    parse(value: unknown) {
      if (value === null || value === undefined) {
        return { ok: true, value: {} };
      }
      if (isPlainObject(value)) {
        return { ok: true, value };
      }
      return { ok: false, errors: ["config must be an object"] };
    },
  };
}
